import { usePostApi } from '@/apiError/apiCreate';
import { useUpdateApi } from '@/apiError/apiUpdate';
import { AntButton } from '@/common/ant-components/AntButton';
import {
  rotateTabWithinForm,
  useInputRef,
} from '@/utils/validation/FormValidation';
import { Col, Form, Input, Row, Space } from 'antd';
import { useEffect, useState } from 'react';
import InventoryGroupFormSkeleton from './InventoryGroupFormSkeleton';
import { useMutateGetApi } from '@/apiError/apiShow';

/**
 * InventoryGroupForm Component
 * Handles both creation and editing of inventory groups.
 * Props:
 * - mode: "create" | "edit" (Determines the behavior of the form)
 */
const InventoryGroupForm = ({
  referenceId,
  onClose = () => {},
  refreshTable = () => {},
  form: propForm,
}) => {
  const { firstInputRef, lastInputRef } = useInputRef();
  const [form] = propForm ? [propForm] : Form.useForm();
  const [loading, setLoading] = useState(false);
  const { mutateAsync: createItem, isPending: createLoading } = usePostApi({
    url: 'account/inventory/groups/',
  });
  const { mutateAsync: updateData, isPending: updateLoading } = useUpdateApi();
  const { mutateAsync: getDataById, isPending: showLoading } =
    useMutateGetApi();
  /**
   * Handles the submission of the form for both creation and updating.
   * Sends an API request based on the mode.
   */
  const handleSubmit = async (formValues) => {
    try {
      // setLoading(true);
      if (referenceId) {
        await updateData({
          url: `/account/inventory/group/${referenceId}/`,
          values: formValues,
        });
      } else {
        await createItem(formValues);
      }
      onClose();
      form.resetFields();
      refreshTable();
    } finally {
      // setLoading(false);
    }
  };

  /**
   * Fetches data for an existing inventory group in edit mode.
   */
  useEffect(() => {
    if (referenceId) {
      const fetchInventoryGroupData = async () => {
        try {
          // setLoading(true);
          const response = await getDataById({
            url: `account/inventory/group/${referenceId}/`,
          });
          form.setFieldsValue({ name: response?.data?.name });
        } finally {
          // setLoading(false);
        }
      };
      fetchInventoryGroupData();
    }
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, [referenceId]);

  const handleFormKeyDown = (e) => {
    rotateTabWithinForm(e, firstInputRef, lastInputRef);
  };

  useEffect(() => {
    form.getFieldInstance('name')?.focus();
  }, []);

  return showLoading ? (
    <InventoryGroupFormSkeleton />
  ) : (
    <>
      {/* <LoadingOverlay loading={loading} /> */}
      <Form
        form={form}
        initialValues={{ name: '' }}
        layout="vertical"
        onFinish={handleSubmit}
        onKeyDown={handleFormKeyDown}
      >
        <Row gutter={24} style={{ marginBottom: '16px' }}>
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              name="name"
              label="Group Name"
              className="username"
              rules={[
                {
                  required: true,
                  message: 'Please enter the group name!',
                },
              ]}
            >
              <Input type="text" />
            </Form.Item>
          </Col>
        </Row>
        <div style={{ textAlign: 'right' }}>
          <Space size="large">
            <AntButton
              color="green"
              type="primary"
              htmlType="submit"
              loading={createLoading || updateLoading}
            >
              {referenceId ? 'Update' : 'Create'}
            </AntButton>
            <AntButton color="red" onClick={onClose}>
              Cancel
            </AntButton>
          </Space>
        </div>
      </Form>
    </>
  );
};

export default InventoryGroupForm;
